// src/ui/Timeline.js
//
// The arrangement: a ruler of bars, one lane per track, and the clips on each.
//
// Every clip is a button, so a clip is reachable by Tab and names itself: what
// it is, where it starts, how long it is, and what it holds. The keyboard does
// everything the pointer does:
//
//   Left and Right move a clip by a beat. Shift with them makes it shorter or
//   longer by a beat. Enter opens it. Delete removes it. S cuts it in two at
//   the playhead, D puts a copy straight after it, M mutes or unmutes it, L locks or unlocks it, [ and ] trim its start or its end to the playhead, and Ctrl+C and Ctrl+X copy or cut
//   the clip (or the selected clips) and Ctrl+V pastes at the playhead.
//
// A drag moves a clip by whole beats, and a drag on its right edge resizes it.
// Both listen for the move and the release on the document once the pointer
// is down, not on the clip: a drag that stops at the clip's own edge is the
// failure CLAUDE.md names, and it passes every test run without a renderer.
//
// Zoom and snap belong to a TimeView, shared with anything else drawn against
// the same beats. The controls above the lanes (Zoom out, Zoom in, Fit, Snap)
// are built once and outside the lanes, so a redraw never takes the focus off
// one. Plus and minus zoom from the keyboard, Ctrl with the wheel from the
// pointer, and Alt held during a drag bypasses the snap.
//
// The loop is drawn under the ruler as a brace with a handle at each end. A
// handle moves with the pointer or with the arrow keys (one grid step; Shift
// for a bar), and a drag on the empty row draws a new loop and turns it on.
// Whether it is on is said in text, not only by colour. With Follow on, the
// lanes scroll to keep the playhead in view while the transport runs, and
// Follow turns itself off when the person scrolls, since they are looking
// elsewhere.
//
// The lanes scroll inside their own box, so a long arrangement never makes the
// page scroll sideways (CLAUDE.md: no horizontal scrolling). Width is beats
// times a scale, which is what a timeline is; the box it scrolls in is sized
// by the page.

import { createTrackHeader } from './TrackHeader.js'
import { createChainStrip } from './ChainStrip.js'
import { createEnvelopeLanes } from './EnvelopeLanes.js'
import { createMasterRow } from './MasterRow.js'
import { createReorder } from './TimelineReorder.js'
import { createLoopRow } from './LoopRow.js'
import { createRuler } from './Ruler.js'
import { createTimelineTools } from './TimelineTools.js'
import { Selection } from '../model/Selection.js'
import { barBeat, describeClip } from './ClipText.js'
import { createClipButton } from './ClipButton.js'
import { createLaneResize } from './LaneResize.js'
import { attachMenu } from './ContextMenu.js'
import { TimeView, DEFAULT_PIXELS_PER_BEAT } from './TimeView.js'
import { DEFAULT_LANE_SIZE } from '../model/EditorState.js'

// The text a clip says about itself is ClipText.js and the clip itself is ClipButton.js; both are re-exported here so
// callers keep one front door.
export { barBeat, describeClip }

/** CSS pixels per beat at the default zoom. */
export const PIXELS_PER_BEAT = DEFAULT_PIXELS_PER_BEAT

/**
 * Build a timeline.
 *
 * Handlers, each a request the page sends through the dispatcher:
 * - `onAdd(trackId, startBeat)`: a new MIDI clip.
 * - `onAddAudio(trackId, startBeat)`: a new audio clip, from a file the page
 *   asks the person for.
 * - `onMove(clipId, startBeat)` and `onResize(clipId, lengthBeats)`.
 * - `onLaneHeight(trackId, height)` and `onLaneReset(trackId)`: a lane dragged to a height of its own, and a double click on its grip to put it back.
 *   Optional: without `onLaneHeight` a lane has no grip.
 * - `onLoops()`: open the loop library. Optional: without it the toolbar has no Loops button.
 * - `onRepeat(clipId, lengthBeats)` and `onStopRepeat(clipId)`: make a MIDI clip that long by repeating its pattern, and stop it
 *   repeating. Optional: without `onRepeat` a clip has no loop handle, no R key and no repeat menu items.
 * - `onOpen(clipId)` and `onRemove(clipId)`.
 * - `onSplit(clipId)` and `onDuplicate(clipId)`: the page knows the playhead.
 * - `onLock(clipId, locked)`: protect a clip from being moved, edited or deleted.
 * - `onMute(clipId, muted)`: keep a clip on the lane but do not play it.
 * - `onCopy(clipId)`, `onCut(clipId)` and `onPaste(clipId)`: Ctrl or Cmd with C, X and V; the page holds the clipboard.
 * - `onAutomateMaster(kind)`: add a lane for the master's 'masterGain' or 'masterPan'.
 * - `onEnvelope(envelopeId, points)` and `onRemoveEnvelope(envelopeId)`: an automation lane edited, or taken away.
 * - `onMoveInChain(nodeId, delta)`: move a plugin one place earlier (-1) or later (1) in its audio chain.
 * - `onBypass(nodeId, bypassed)`: take a plugin out of the signal, or put it back.
 * - `onTrim(clipId, edge)`: shorten a clip to the playhead, from its 'start' or its 'end'.
 * - `onChannel(trackId, change)`: level, pan, mute or solo, the part that changed.
 * - `onArm(trackId, on)`: take MIDI input, or stop.
 * - `onMoveTrack(trackId, delta)`: move a track up (-1) or down (1).
 * - `onFolderCollapse(folderName, collapsed)`: hide or show every track of a folder. Whether a folder is hidden is read
 *   back from each track's layout (`folderCollapsed`), so it is the caller's to keep and save.
 * - `onAddTrack()`: add an empty track. Optional: without it the toolbar has no Add track.
 * - `onDuplicateTrack(trackId)`: copy a track with its plugins. Optional: without it a track has no Duplicate button.
 * - `onSetLoop({ start, end })`: a new loop range in beats, which the page also turns on.
 */
export function createTimeline (document, { onAdd, onAddAudio, onMove, onResize, onOpen, onRemove, onSplit, onDuplicate, onBypass, onMoveInChain, onEnvelope, onRemoveEnvelope, onAutomateMaster, onMute, onTrim, onCopy, onCut, onPaste, onLock, onChannel, onSetLoop, onMoveTrack, onFolderCollapse, onArm, onArmSource = null, onSeek = null, onReplace = null, onDuplicateTrack = null, onAddTrack = null, onExpandTrack = null, onSizeStep = null, onEditTrack = null, onDeleteTrack = null, onRemoveNode = null, onShowNode = null, onRepeat = null, onStopRepeat = null, onLoops = null, onLaneHeight = null, onLaneReset = null }, { view = new TimeView(), selection = new Selection(), menu = null } = {}) {
  for (const [name, fn] of Object.entries({ onAdd, onAddAudio, onMove, onResize, onOpen, onRemove, onSplit, onDuplicate, onBypass, onMoveInChain, onEnvelope, onRemoveEnvelope, onAutomateMaster, onMute, onTrim, onCopy, onPaste, onChannel, onSetLoop, onMoveTrack, onFolderCollapse, onArm })) {
    if (typeof fn !== 'function') throw new Error(`createTimeline needs ${name}`)
  }
  const element = document.createElement('div')
  element.className = 'timeline'
  const scroller = document.createElement('div')
  scroller.className = 'timeline-scroll'
  // Focusable, so the lanes can be scrolled by keyboard (WCAG 2.1.1).
  scroller.tabIndex = 0
  scroller.setAttribute('role', 'region')
  scroller.setAttribute('aria-label', 'Arrangement, scrolls sideways')
  const tools = createTimelineTools(document, {
    view, onSeek, onAddTrack, onLoops, bpb: () => bpb(), zoom: factor => zoom(factor), fit: () => fit(),
    onRouting: on => { element.dataset.routing = on ? 'on' : 'off' }
  })
  element.append(tools.element, scroller)
  // What a reorder did, in words, for a screen reader: a track that moves is drawn somewhere else and nothing says where.
  const said = document.createElement('div')
  said.className = 'visually-hidden'
  said.setAttribute('role', 'status')
  said.setAttribute('aria-live', 'polite')
  element.append(said)
  // Where the transport is. Drawn over the lanes, never read: the position
  // readout in the transport bar says the same in words.
  const head = document.createElement('div')
  head.className = 'playhead'
  head.setAttribute('aria-hidden', 'true')
  head.hidden = true

  /**
   * `peaksFor(clip, count)` gives an audio clip's waveform as `count` peaks
   * from 0 to 1, or null while it is not loaded; `unplayable(clip)` gives why
   * an audio clip cannot play, or null.
   */
  let lastArgs = null
  // The scroll position this file last set itself, so its own scrolling is not taken for the person's.
  let programmatic = null
  // Kept for as long as the timeline is: the ruler is rewritten in place and a
  // track's row is reused, so a redraw moves nothing that has not changed.
  const ruler = createRuler(document, { ppb: () => ppb(), onSeek: onSeek && (beat => onSeek(beat)), snapBeat: (beat, e) => snapBeat(beat, e) })
  const rows = new Map()
  // The tracks whose plugins are shown under their lane by the FX button. Kept here and not in the session: a view, not a document.
  const fxOpen = new Set()
  function toggleFx (id) {
    if (fxOpen.has(id)) fxOpen.delete(id); else fxOpen.add(id)
    if (lastArgs) draw(lastArgs)
  }
  // The master's own row after the tracks: its automation lanes and the way to add one.
  const masterRow = createMasterRow(document, { onChange: onEnvelope, onRemove: onRemoveEnvelope, onAutomate: onAutomateMaster, view })
  const ppb = () => view.pixelsPerBeat
  const bpb = () => lastArgs?.beatsPerBar ?? 4
  const snapBeat = (beat, e) => view.snap(beat, bpb(), { bypass: e?.altKey })
  const loopRow = createLoopRow(document, { view, bpb, onSetLoop, snapBeat })
  const headPx = () => scroller.querySelector('.timeline-head')?.offsetWidth ?? 0

  /** Zoom by `factor`, keeping the beat under `anchorX` (from the lanes' left edge) where it is. */
  function zoom (factor, anchorX = 0) {
    const beat = (scroller.scrollLeft + anchorX) / ppb()
    view.zoomBy(factor)
    scroller.scrollLeft = beat * ppb() - anchorX
    programmatic = scroller.scrollLeft
  }

  function fit () {
    if (!lastArgs) return
    const width = scroller.clientWidth - headPx()
    if (!(width > 0)) return
    const last = Math.max(bpb() * 4, ...lastArgs.clips.map(c => c.startBeat + c.lengthBeats))
    view.fit(last, width)
    scroller.scrollLeft = 0
    programmatic = 0
  }


  // A selection changes what is marked, never what is drawn, so the lanes are
  // not rebuilt and the focus stays where it is.
  selection.subscribe(() => {
    for (const button of scroller.querySelectorAll('.clip')) {
      const on = selection.has('clip', button.id.replace(/^clip-/, ''))
      button.classList.toggle('selected', on)
      button.setAttribute('aria-current', String(on))
    }
    for (const chip of scroller.querySelectorAll('.chain-node')) {
      chip.setAttribute('aria-pressed', String(selection.has('node', chip.id.replace(/^chain-/, ''))))
    }
    for (const [id, entry] of rows) {
      const on = selection.has('track', id)
      entry.header.element.classList.toggle('selected', on)
      entry.header.element.querySelector('.show-track')?.setAttribute('aria-pressed', String(on))
    }
  })

  view.subscribe(() => {
    tools.sync()
    if (lastArgs) draw(lastArgs)
  })
  scroller.addEventListener('keydown', event => {
    if (event.target !== scroller || event.ctrlKey || event.metaKey || event.altKey) return
    if (event.key === '+' || event.key === '=') { event.preventDefault(); zoom(1.5) }
    else if (event.key === '-') { event.preventDefault(); zoom(1 / 1.5) }
  })
  scroller.addEventListener('wheel', event => {
    if (!event.ctrlKey) return
    event.preventDefault()
    const rect = scroller.getBoundingClientRect()
    zoom(event.deltaY < 0 ? 1.15 : 1 / 1.15, event.clientX - rect.left - headPx())
  }, { passive: false })

  const clipButton = createClipButton(document, { ppb, view, selection, onMove, onResize, onOpen, onRemove, onSplit, onDuplicate, onMute, onCopy, onCut, onPaste, onLock, onTrim, onRepeat, onStopRepeat, menu })

  const reorder = createReorder(document, { rows, current: () => lastArgs, scroller, selection, say: text => { said.textContent = text }, onMoveTrack, onFolderCollapse })
  const { withFolders, moveTrack, dragTrack, extendTrackSelection } = reorder

  function draw (args) {
    lastArgs = args
    // How much of the lanes is on screen, for anything that must wrap inside it.
    scroller.style.setProperty('--view', `${scroller.clientWidth}px`)
    const {
      tracks, clips, beatsPerBar, labelFor, playsIntoNothing, peaksFor = () => null, unplayable = () => null,
      mixable = () => true, silent = () => false, loop = null, lanesFor = () => [], masterFor = () => ({ list: [], available: [] }),
      layoutFor = () => ({ color: null, laneSize: DEFAULT_LANE_SIZE }),
      colorFor = () => null,
      latencyFor = () => null,
      chainFor = () => ({ nodes: [] }),
      kindFor = () => null, canArm = () => false, armed = () => false, routingFor = () => null,
      empty: emptyState = { text: 'No tracks yet. Load a plugin and its track appears here.', actions: [], groups: [] }
    } = args
    const lastBeat = Math.max(0, ...clips.map(c => c.startBeat + c.lengthBeats))
    // Room for a few bars past the last clip, so there is always somewhere to put the next.
    const bars = Math.ceil(lastBeat / beatsPerBar) + 4
    const width = bars * beatsPerBar * ppb()

    ruler.draw({ bars, beatsPerBar, width })

    if (tracks.length === 0) {
      rows.clear()
      const empty = document.createElement('div')
      empty.className = 'empty'
      const text = document.createElement('p')
      text.textContent = emptyState.text
      empty.append(text)
      // Groups, each a heading over its buttons (src/ui/StartScreen.js): a named group for a screen reader, a row for the eye.
      for (const [index, group] of (emptyState.groups ?? []).entries()) {
        const box = document.createElement('div')
        box.className = 'empty-group'
        box.setAttribute('role', 'group')
        const heading = document.createElement('h3')
        heading.id = `empty-group-${index}`
        heading.textContent = group.heading
        box.setAttribute('aria-labelledby', heading.id)
        box.append(heading)
        for (const action of group.actions) {
          const button = document.createElement('button')
          button.type = 'button'
          button.textContent = action.label
          button.addEventListener('click', () => action.run())
          box.append(button)
        }
        empty.append(box)
      }
      // Real actions, each the same request the page makes elsewhere.
      for (const action of emptyState.actions ?? []) {
        const button = document.createElement('button')
        button.type = 'button'
        button.textContent = action.label
        button.addEventListener('click', () => action.run())
        empty.append(button)
      }
      loopRow.draw(loop, width)
      scroller.replaceChildren(head, ruler.element, loopRow.element, empty)
      return
    }

    // A row is kept for as long as its track is, and only its lane is redrawn.
    // The header holds sliders, and a slider dragged while it is taken out of
    // the document loses the pointer (src/ui/TrackHeader.js).
    for (const id of [...rows.keys()]) if (!tracks.some(t => t.id === id)) rows.delete(id)
    const wanted = tracks.map(track => {
      const label = labelFor(track)
      let entry = rows.get(track.id)
      if (!entry) {
        const row = document.createElement('div')
        row.className = 'timeline-row'
        row.dataset.track = track.id
        row.setAttribute('role', 'group')
        const header = createTrackHeader(document, {
          id: track.id, onAdd, onAddAudio, onChannel, onMove: moveTrack, onArm, onArmSource, onDuplicateTrack, onGrip: dragTrack, onFx: toggleFx, onExpand: onExpandTrack, onSizeStep, onExtend: extendTrackSelection, onEdit: onEditTrack, onDeleteTrack, menu,
          onSelect: (id, { toggle }) => { reorder.anchor(id); if (toggle) selection.toggle('track', id); else selection.set('track', [id]) }
        })
        const lane = document.createElement('div')
        lane.className = 'timeline-lane'
        // The grip to drag the lane taller or shorter, only where the page can keep a height; plus and minus do it by size without the pointer.
        const grip = onLaneHeight ? createLaneResize(document, { lane, onCommit: height => onLaneHeight(track.id, height), onReset: () => onLaneReset?.(track.id) }) : null
        // The header and lane sit side by side; the chain strip is under both.
        const body = document.createElement('div')
        body.className = 'timeline-body'
        body.append(header.element, lane)
        const strip = createChainStrip(document, { onSelect: id => selection.set('node', [id]), onBypass, onMove: onMoveInChain, onReplace, onRemove: onRemoveNode, onShow: onShowNode, menu })
        const automation = createEnvelopeLanes(document, { onChange: onEnvelope, onRemove: onRemoveEnvelope, view })
        // Double click on empty lane adds a MIDI clip there, on the grid; right click offers that and an audio file. A clip stops
        // its own contextmenu event, and a double click on one lands on the clip, not here.
        const beatAt = event => Math.max(0, snapBeat((event.clientX - lane.getBoundingClientRect().left) / ppb(), event))
        lane.addEventListener('dblclick', event => { if (event.target === lane) onAdd(track.id, beatAt(event)) })
        if (menu) {
          attachMenu(lane, menu, event => {
            if (event.target !== lane) return null
            const at = beatAt(event)
            return [
              { label: 'Add a MIDI clip here', run: () => onAdd(track.id, at) },
              { label: 'Add an audio file here', run: () => onAddAudio(track.id, at) }
            ]
          })
        }
        row.append(body, automation.element, strip.element)
        entry = { row, header, lane, grip, strip, automation }
        rows.set(track.id, entry)
      }
      const own = clips.filter(c => c.track === track.id)
      const end = Math.max(0, ...own.map(c => c.startBeat + c.lengthBeats))
      // After the last clip, rounded up to a bar, so a new clip never covers one.
      const at = Math.ceil(end / beatsPerBar) * beatsPerBar
      entry.row.setAttribute('aria-label', `Track ${label}`)
      const layout = layoutFor(track)
      entry.row.dataset.size = layout.laneSize
      const chain = chainFor(track)
      entry.row.dataset.fx = fxOpen.has(track.id) && chain.nodes.length > 0 ? 'on' : 'off'
      // A height of its own, dragged to, replaces the height of the size; a collapsed lane stays a bar.
      if (layout.laneHeight && layout.laneSize !== 'collapsed') entry.lane.style.minHeight = `${layout.laneHeight}px`
      else entry.lane.style.removeProperty('min-height')
      if (layout.color) entry.row.style.setProperty('--track-color', layout.color); else entry.row.style.removeProperty('--track-color')
      entry.header.update({
        label, channel: track.channel ?? {}, mixable: mixable(track), silent: silent(track), at, where: barBeat(at, beatsPerBar), selected: selection.has('track', track.id),
        color: layout.color, size: layout.laneSize, first: tracks[0] === track, last: tracks.at(-1) === track, latency: latencyFor(track), kind: kindFor(track), canArm: canArm(track), armed: armed(track), routing: routingFor(track), fx: entry.row.dataset.fx === 'on', fxCount: chain.nodes.length
      })
      entry.strip.update(chain, { label, selected: selection.kind === 'node' ? selection.ids[0] : null })
      entry.automation.update(lanesFor(track), { beatsPerBar, width })
      entry.lane.style.width = `${width}px`
      entry.lane.style.backgroundSize = `${beatsPerBar * ppb()}px 100%`
      entry.lane.replaceChildren(...own.map(clip => clipButton(clip, {
        beatsPerBar,
        playsIntoNothing: clip.kind === 'midi' && playsIntoNothing(track),
        color: colorFor(clip),
        peaks: clip.kind === 'audio' ? peaksFor(clip, Math.max(1, Math.round(clip.lengthBeats * ppb() / 3))) : null,
        problem: clip.kind === 'audio' ? unplayable(clip) : null
      })))
      // The clips are replaced on every draw, and the grip is not one of them.
      if (entry.grip) entry.lane.append(entry.grip)
      return entry.row
    })
    // Only when the list differs, so a redraw that changes no track's place
    // moves nothing (and so drops no focus).
    const current = [...scroller.children]
    loopRow.draw(loop, width)
    masterRow.update(masterFor(), { beatsPerBar, width })
    const expected = [head, ruler.element, loopRow.element, ...withFolders(tracks, wanted, layoutFor), masterRow.element]
    const same = current.length === expected.length && current.every((child, k) => child === expected[k])
    if (!same) scroller.replaceChildren(...expected)
    // The playhead is as tall as everything that scrolls, not as the box it is seen through: the box is shorter than the
    // lanes now, and a head the height of the box would scroll away with the first track.
    head.style.height = `${scroller.scrollHeight}px`
  }

  /** Show the transport at `beat`, or nothing for null. */
  function playhead (beat) {
    head.hidden = beat === null
    if (beat === null) return
    head.style.left = `calc(var(--head) + ${beat * ppb()}px)`
    if (!tools.following()) return
    // Page-flip: when the head leaves the visible lanes, bring it back a
    // little in from the left, rather than nudging every frame.
    const visible = scroller.clientWidth - headPx()
    const x = beat * ppb()
    if (visible > 0 && (x < scroller.scrollLeft || x > scroller.scrollLeft + visible)) {
      scroller.scrollLeft = Math.max(0, x - visible * 0.15)
      programmatic = scroller.scrollLeft
    }
  }
  // Scrolling that this file did not do is the person looking elsewhere.
  scroller.addEventListener('scroll', () => {
    if (tools.following() && !head.hidden && (programmatic === null || Math.abs(scroller.scrollLeft - programmatic) > 1)) tools.setFollow(false)
    programmatic = null
  })

  // The view is handed back so the page can snap a cut to the same grid a drag snaps to.
  return { element, draw, playhead, view }
}
